<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Time Sheet" class="px-4 pr-5 mb-5">
      </SimplePageHeader>
      <v-row
        align="center"
        justify="start"
        class="px-5 mb-2 pb-3"
      >
        <v-col cols="12" sm="5" md="4" lg="3">
          <WeekPicker
            :date="dateFilter"
            :hideDetails="true"
            @weekChange="weekChangeHandler"
            @dateChange="(newValue) => this.dateFilter = newValue"
          ></WeekPicker>
        </v-col>
        <v-col cols="12" sm="7" md="8" lg="5" xl="4">
          <v-select
            label="Contract"
            v-model="selectedAssignmentId"
            :items="assignmentsOptionsList"
            item-text="text"
            item-value="value"
            outlined
            hide-details
            @change="selectedAssignmentChangeHandler"
          ></v-select>
        </v-col>
        <v-col cols="12" md="6" lg="4" xl="3" style="color: rgba(0,0,0,.6);" v-if="!showNoDataMessage">
          <v-chip
            color="primary"
            class="ml-1 py-5 px-3"
          >
            Total:&nbsp;&nbsp;{{ weekTotals.workedHours.toFixed(2) }} Hours
            &nbsp;&nbsp;
            <span v-if="weekTotals.timeOffHours > 0">Time Off:&nbsp;&nbsp;{{ weekTotals.timeOffHours.toFixed(2) }} Hours&nbsp;&nbsp;</span>
            <span v-if="contractorCommission > 0">Commission:&nbsp;&nbsp;${{ parseFloat(contractorCommission).toFixed(2) }}</span>
          </v-chip>
        </v-col>
      </v-row>
    </div>
    <v-container fluid class="px-0 pt-2">
      <v-card style="max-height: 590px; overflow-y: auto; overflow-x: hidden;" v-if="!showNoDataMessage">
        <v-card-text>
          <v-row>
            <v-col
              class="px-1 time-sheet-day-size"
              v-for="day in timeSheetDays" :key="day.date"
            >
              <ConsultantTimeSheetEntry
                :day="day"
                @entryTimeChange="entryTimeChangeHandler"
              ></ConsultantTimeSheetEntry>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <div v-if="showNoDataMessage">
        <WithoutAssignmentEntriesView
        ></WithoutAssignmentEntriesView>
      </div>
    </v-container>
    <ButtonsSubmitForm
      v-if="currentOrUpcomingWeek && !showNoDataMessage"
      :isFormValid="true"
      acceptLabel="Save"
      cancelLabel="Discard"
      @save="saveTimeSheetEntriesHandler"
      @cancel="discardChangesHandler"
      >
    </ButtonsSubmitForm>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { mapActions, mapState } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer, ButtonsSubmitForm } from '@/components/buttons'
import { WeekPicker } from '@/components/shared/datePickers'
import { getCurrentUser } from '@/lib/authentication'
import { nextTick } from 'vue'
import WithoutAssignmentEntriesView from './WithoutAssignmentEntriesView'
import ConsultantTimeSheetEntry from './ConsultantTimeSheetEntry'
import { formatDate } from '@/lib/display'
import { timeOffTypesToShow, buildTimeSheetDays, changedEntries, weekTotals } from './consultantTimeOffHelpers'

export default {
  components: {
    SimplePageHeader,
    BottomSpacer,
    WeekPicker,
    WithoutAssignmentEntriesView,
    ConsultantTimeSheetEntry,
    ButtonsSubmitForm
  },
  data () {
    return {
      currentWeekDateRange: null,
      dateFilter: null,
      dateBeginFilter: null,
      dateEndFilter: null,
      assignmentsOptionsList: [],
      assignmentsList: [],
      selectedAssignmentId: null,
      showNoDataMessage: true,
      currentUser: null,
      timeSheetEntries: [],
      timeSheetDays: []
    }
  },
  computed: {
    ...mapFields('consultantTimeSheet', [
      'consultantTimeSheetEntries',
      'contractorCommission'
    ]),
    ...mapState('timeTypes', ['timeTypesList']),
    timeOffTypes () {
      const assignment = this.assignmentsList.find(a => a.id === this.selectedAssignmentId)
      return assignment ? timeOffTypesToShow(assignment.employeeTypeId, this.timeTypesList) : []
    },
    weekTotals () {
      return weekTotals(this.timeSheetDays, this.timeTypesList)
    },
    currentOrUpcomingWeek () {
      if (this.currentWeekDateRange && this.dateBeginFilter) {
        return new Date(this.dateBeginFilter) >= new Date(this.currentWeekDateRange.beginDate)
      }
      return false
    }
  },
  watch: {
    timeSheetEntries () {
      this.timeSheetDays = buildTimeSheetDays(this.timeSheetEntries, this.timeOffTypes)
      this.showNoDataMessage = !(this.timeSheetEntries && this.timeSheetEntries.length > 0)
    },
    timeOffTypes () {
      this.timeSheetDays = buildTimeSheetDays(this.timeSheetEntries, this.timeOffTypes)
    }
  },
  methods: {
    ...mapActions('assignments', [
      'getAssignmentsByUserId'
    ]),
    ...mapActions('datetimeHelper', [
      'getStaffLynxTime',
      'getUTCTime',
      'getStaffLynxCurrentWeekDateRange'
    ]),
    ...mapActions('consultantTimeSheet', [
      'getTimeSheetEntries',
      'saveTimeSheetEntries',
      'getContractorCommission'
    ]),
    ...mapActions('timeTypes', [
      'getTimeTypes'
    ]),
    ...mapActions('staffTimeSheet', [
      'getStaffTimeSheetEntries',
      'saveStaffTimeSheetEntries'
    ]),
    async weekChangeHandler (dateBegin, dateEnd) {
      this.dateBeginFilter = dateBegin
      this.dateEndFilter = dateEnd
      this.loadTimeSheetEntries().then(async () => {
        await this.getContractorCommission(
          {
            assignmentId: this.selectedAssignmentId,
            staffLynxLocalDateBegin: this.dateBeginFilter,
            staffLynxLocalDateEnd: this.dateEndFilter
          }
        )
      })
    },
    async selectedAssignmentChangeHandler () {
      this.loadTimeSheetEntries().then(async () => {
        await this.getContractorCommission(
          {
            assignmentId: this.selectedAssignmentId,
            staffLynxLocalDateBegin: this.dateBeginFilter,
            staffLynxLocalDateEnd: this.dateEndFilter
          }
        )
      })
    },
    entryTimeChangeHandler (row, newHoursValue) {
      row.entry.hours = newHoursValue
    },
    saveTimeSheetEntriesHandler () {
      const entriesToSave = changedEntries(this.timeSheetDays)
      if (entriesToSave.length > 0) {
        if (this.currentUser.staffEmployeeId != null && this.currentUser.staffNeedTimeSheet) {
          this.saveStaffTimeSheetEntries(entriesToSave).then(() => {
            this.loadTimeSheetEntries()
          })
        } else {
          this.saveTimeSheetEntries(entriesToSave).then(() => {
            this.loadTimeSheetEntries()
          })
        }
      }
    },
    discardChangesHandler () {
      if (changedEntries(this.timeSheetDays).length > 0) {
        this.loadTimeSheetEntries()
      }
    },
    async loadTimeSheetEntries () {
      if (this.selectedAssignmentId && this.dateBeginFilter && this.dateEndFilter) {
        if (this.currentUser.staffEmployeeId != null && this.currentUser.staffNeedTimeSheet) {
          this.timeSheetEntries = await this.getStaffTimeSheetEntries({
            staffId: this.selectedAssignmentId.substring(1),
            staffLynxLocalDateBegin: this.dateBeginFilter,
            staffLynxLocalDateEnd: this.dateEndFilter
          })
        } else {
          this.timeSheetEntries = await this.getTimeSheetEntries({
            assignmentId: this.selectedAssignmentId,
            staffLynxLocalDateBegin: this.dateBeginFilter,
            staffLynxLocalDateEnd: this.dateEndFilter
          })
        }
      }
    }
  },
  async created () {
    this.currentUser = await getCurrentUser()
    // Add the Internal Employee assignment if staffEmployeeId different from null
    if (this.currentUser.staffEmployeeId != null && this.currentUser.staffNeedTimeSheet) {
      // Add a S to avoid repeating the same Id of an assignment.
      this.assignmentsOptionsList.push({
        value: 'S' + this.currentUser.staffEmployeeId,
        text: 'Internal Employee - ' + formatDate(this.currentUser.staffEmployeeStartDate)
      })
      this.selectedAssignmentId = 'S' + this.currentUser.staffEmployeeId
    }
    this.currentWeekDateRange = await this.getStaffLynxCurrentWeekDateRange()
    this.getTimeTypes()
    let assignmentsList = await this.getAssignmentsByUserId(getCurrentUser().id)
    assignmentsList = assignmentsList.filter(a => a.timeSheetType === 2)
    this.assignmentsList = assignmentsList
    this.showNoDataMessage = !(assignmentsList && assignmentsList.length > 0)
    if (!this.showNoDataMessage) {
      this.selectedAssignmentId = assignmentsList[0].id
      this.assignmentsOptionsList = assignmentsList.map(assignment => {
        return {
          value: assignment.id,
          text: assignment.clientName + ' - ' + assignment.jobTitle + ' - ' + formatDate(assignment.startAt)
        }
      })
      await nextTick()
      if (this.selectedAssignmentId) {
        this.loadTimeSheetEntries().then(async () => {
          await this.getContractorCommission(
            {
              assignmentId: this.selectedAssignmentId,
              staffLynxLocalDateBegin: this.dateBeginFilter,
              staffLynxLocalDateEnd: this.dateEndFilter
            }
          )
        })
      }
    }
  }
}
</script>
<style>
  @media only screen and (max-width: 600px) {
    .time-sheet-day-size {
      min-width: 50% !important;
      max-width: 50% !important;
    }
  }

  @media only screen and (min-width: 601px) and (max-width: 768px) {
    .time-sheet-day-size {
      min-width: 25% !important;
      max-width: 25% !important;
    }
  }

  @media only screen and (min-width: 769px) {
    .time-sheet-day-size {
      min-width: 14.28% !important;
      max-width: 14.28% !important;
    }
  }
</style>
